import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { Box, Button, ButtonGroup, Typography, Card, Grid, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { api, authenticatedFetch } from '../utils/api';

function ProgressDashboard() {
  const [checkins, setCheckins] = useState([]);
  const [weightGoal, setWeightGoal] = useState("");
  const [goalInput, setGoalInput] = useState("");
  const [goalMessage, setGoalMessage] = useState("");
  const [goalLoading, setGoalLoading] = useState(false);
  const [viewPeriod, setViewPeriod] = useState('week');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchCheckins();
    fetchGoal();
  }, []);

  const fetchCheckins = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(api.checkins, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setCheckins(Array.isArray(data) ? data.reverse() : []);
  };

  const fetchGoal = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(api.weightGoal, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.weightGoal !== undefined && data.weightGoal !== null) {
      setWeightGoal(data.weightGoal);
      setGoalInput(data.weightGoal);
    }
  };

  const handleGoalSubmit = async (e) => {
    e.preventDefault();
    setGoalLoading(true);
    setGoalMessage("");
    const token = localStorage.getItem('token');
    const response = await fetch(api.weightGoal, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ weightGoal: goalInput })
    });
    const data = await response.json();
    setGoalLoading(false);
    if (response.ok) {
      setWeightGoal(data.weightGoal);
      setGoalMessage('Weight goal updated!');
    } else {
      setGoalMessage(data.error || 'Failed to update weight goal');
    }
  };

  const getFilteredData = () => {
    const now = new Date();
    let startDate, endDate;

    switch (viewPeriod) {
      case 'week':
        startDate = startOfWeek(now);
        endDate = endOfWeek(now);
        break;
      case 'month':
        startDate = startOfMonth(new Date(selectedYear, selectedMonth));
        endDate = endOfMonth(new Date(selectedYear, selectedMonth));
        break;
      case 'all':
        return checkins;
      default:
        return checkins;
    }

    return checkins.filter(checkin => {
      const checkinDate = new Date(checkin.timestamp);
      return checkinDate >= startDate && checkinDate <= endDate;
    });
  };

  const moodToNum = mood => {
    switch (mood) {
      case 'Excellent': return 5;
      case 'Good': return 4;
      case 'Okay': return 3;
      case 'Poor': return 2;
      case 'Terrible': return 1;
      default: return null;
    }
  };
  
  const numToMood = num => ['Terrible','Poor','Okay','Good','Excellent'][num-1] || 'N/A';

  const filteredCheckins = getFilteredData();
  const chartData = filteredCheckins.map(c => ({
    date: viewPeriod === 'week' ? format(new Date(c.timestamp), 'EEE MMM d') : format(new Date(c.timestamp), 'MMM d'),
    mood: moodToNum(c.mood),
    energy: c.energy,
    sleep: c.sleepHours,
    soreness: c.soreness,
    stress: c.stressLevel,
    hydration: c.hydration,
    weight: c.weight,
    workoutMotivation: c.workoutMotivation,
  }));

  const calculateAverages = () => {
    if (filteredCheckins.length === 0) return {};
    
    const totals = filteredCheckins.reduce((acc, checkin) => {
      acc.mood += moodToNum(checkin.mood) || 0;
      acc.energy += checkin.energy || 0;
      acc.sleep += checkin.sleepHours || 0;
      acc.soreness += checkin.soreness || 0;
      acc.stress += checkin.stressLevel || 0;
      acc.hydration += checkin.hydration || 0;
      acc.workoutMotivation += checkin.workoutMotivation || 0;
      acc.count++;
      return acc;
    }, { mood: 0, energy: 0, sleep: 0, soreness: 0, stress: 0, hydration: 0, workoutMotivation: 0, count: 0 });

    return {
      mood: (totals.mood / totals.count).toFixed(1),
      energy: (totals.energy / totals.count).toFixed(1),
      sleep: (totals.sleep / totals.count).toFixed(1),
      soreness: (totals.soreness / totals.count).toFixed(1),
      stress: (totals.stress / totals.count).toFixed(1),
      hydration: (totals.hydration / totals.count).toFixed(1),
      workoutMotivation: (totals.workoutMotivation / totals.count).toFixed(1),
    };
  };

  const averages = calculateAverages();
  const latestWeight = chartData.find(d => d.weight !== undefined && d.weight !== null)?.weight;
  const showProgress = latestWeight && weightGoal;
  const progressPercent = showProgress ? Math.round(100 * (latestWeight / weightGoal)) : 0;

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 3}, (_, i) => currentYear - i);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header with View Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
          📈 Progress Dashboard
        </Typography>
        
        <ButtonGroup variant="outlined" size="large">
          <Button 
            variant={viewPeriod === 'week' ? 'contained' : 'outlined'}
            onClick={() => setViewPeriod('week')}
          >
            This Week
          </Button>
          <Button 
            variant={viewPeriod === 'month' ? 'contained' : 'outlined'}
            onClick={() => setViewPeriod('month')}
          >
            Monthly
          </Button>
          <Button 
            variant={viewPeriod === 'all' ? 'contained' : 'outlined'}
            onClick={() => setViewPeriod('all')}
          >
            All Time
          </Button>
        </ButtonGroup>
      </Box>

      {/* Month/Year Selector */}
      {viewPeriod === 'month' && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Month</InputLabel>
            <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
              {months.map((month, index) => (
                <MenuItem key={index} value={index}>{month}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Summary Cards */}
      {filteredCheckins.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h6" color="primary">Check-ins</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{filteredCheckins.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                {viewPeriod === 'week' ? 'This Week' : viewPeriod === 'month' ? 'This Month' : 'Total'}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h6" color="primary">Avg Energy</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{averages.energy}/10</Typography>
              <Chip size="small" 
                label={Number(averages.energy) >= 7 ? 'Great!' : Number(averages.energy) >= 5 ? 'Good' : 'Needs work'} 
                color={Number(averages.energy) >= 7 ? 'success' : Number(averages.energy) >= 5 ? 'primary' : 'warning'} />
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h6" color="primary">Avg Sleep</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{averages.sleep}h</Typography>
              <Chip size="small" 
                label={Number(averages.sleep) >= 7 ? 'Good sleep!' : 'Need more sleep'} 
                color={Number(averages.sleep) >= 7 ? 'success' : 'warning'} />
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h6" color="primary">Avg Mood</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{numToMood(Math.round(averages.mood))}</Typography>
              <Chip size="small" 
                label={Number(averages.mood) >= 4 ? 'Positive' : 'Could be better'} 
                color={Number(averages.mood) >= 4 ? 'success' : 'warning'} />
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Weight Goal Section */}
      <Card sx={{ mb: 4, p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>🎯 Weight Goal</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box component="form" onSubmit={handleGoalSubmit} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography>Goal (lbs):</Typography>
              <Box component="input"
                type="number" min="0" step="0.1" value={goalInput}
                onChange={e => setGoalInput(e.target.value)}
                sx={{ width: '120px', padding: '8px', borderRadius: '4px', border: 'none' }}
              />
              <Button type="submit" disabled={goalLoading} variant="contained" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                {goalLoading ? 'Saving...' : 'Save'}
              </Button>
            </Box>
            {goalMessage && <Typography sx={{ color: goalMessage.includes('updated') ? '#4caf50' : '#f44336' }}>{goalMessage}</Typography>}
            {weightGoal && <Typography sx={{ mt: 1 }}>Current goal: <strong>{weightGoal} lbs</strong></Typography>}
          </Grid>
          {showProgress && (
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1 }}>Latest weight: <strong>{latestWeight} lbs</strong></Typography>
              <Box sx={{ background: 'rgba(255,255,255,0.3)', borderRadius: '8px', height: '32px', width: '100%', overflow: 'hidden', mb: 1 }}>
                <Box sx={{ width: `${Math.min(progressPercent, 100)}%`, background: '#4caf50', height: '100%', transition: 'width 0.5s' }}></Box>
              </Box>
              <Typography sx={{ fontSize: '0.95rem' }}>Progress: {progressPercent}% of goal</Typography>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Charts */}
      {chartData.length > 0 ? (
        <>
          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Mood & Energy Trends</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[1, 10]} />
                <Tooltip formatter={(value, name) => name === 'mood' ? numToMood(value) : [value, name]} />
                <Legend />
                <Line type="monotone" dataKey="mood" stroke="#8884d8" name="Mood" strokeWidth={3} />
                <Line type="monotone" dataKey="energy" stroke="#82ca9d" name="Energy" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Sleep & Recovery</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sleep" fill="#8884d8" name="Sleep Hours" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Stress, Soreness & Motivation</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="stress" stroke="#ff7300" name="Stress" strokeWidth={2} />
                <Line type="monotone" dataKey="soreness" stroke="#ff1493" name="Soreness" strokeWidth={2} />
                <Line type="monotone" dataKey="workoutMotivation" stroke="#00bcd4" name="Workout Motivation" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {chartData.some(d => d.weight) && (
            <Card sx={{ mb: 3, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Weight Progress</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData.filter(d => d.weight)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: 'lbs', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="weight" stroke="#ff1493" name="Weight (lbs)" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
        </>
      ) : (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No check-ins found for {viewPeriod === 'week' ? 'this week' : viewPeriod === 'month' ? 'this month' : 'the selected period'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Complete some check-ins to see your progress trends!
          </Typography>
        </Card>
      )}
    </Box>
  );
}

export default ProgressDashboard;